import { useEffect, useState } from 'react';
import { Drawer, Portal, CloseButton, Box } from '@chakra-ui/react';
import { FolderOpenIcon } from '@heroicons/react/24/outline';
import PublicContent from '@/components/content-list/public-content';
import { PillGroup } from '@/components/ui/pill-group';
import useDialogCleanup from '@/lib/hooks/use-dialog-cleanup';
import { ContentType, ExtendedWorkspace } from '@/lib/types';
import CarouselEmptyState from '@/components/home/carousel-empty-state';

interface Workspace extends ExtendedWorkspace {
  types: ContentType[];
}

interface Type {
  id: number;
  name: string;
  reference_id: string;
}

interface Props {
  isOpen: boolean;
  onClose(): void;
  workspace: Workspace;
  types: Type[];
}

export default function BrowseContent({ isOpen, onClose, workspace, types }: Props) {
  const [tabIndex, setTabIndex] = useState<string | null>(null);
  useDialogCleanup(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTabIndex(types.length > 0 ? types[0].reference_id : null);
    }
  }, [isOpen, types]);

  return (
    <Drawer.Root
      id={'browse-content-drawer'}
      size='xl'
      placement='end'
      lazyMount
      unmountOnExit
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) onClose();
      }}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.CloseTrigger asChild>
              <CloseButton size='sm' />
            </Drawer.CloseTrigger>
            {workspace && (
              <Drawer.Header>
                <Drawer.Title>{workspace.name}</Drawer.Title>
              </Drawer.Header>
            )}
            <Drawer.Body>
              {types.length === 0 && (
                <CarouselEmptyState
                  icon={FolderOpenIcon}
                  title='No content available'
                  description='This workspace has no content types configured yet.'
                />
              )}
              {tabIndex && (
                <>
                  <Box mb={3} w={'full'}>
                    <PillGroup
                      items={types.map((type) => ({
                        value: type.reference_id,
                        label: type.name,
                      }))}
                      value={tabIndex}
                      onChange={(refId) => setTabIndex(refId)}
                    />
                  </Box>

                  <PublicContent referenceId={tabIndex} />
                </>
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}
